import AppSettings from "../AppSettings";
import { InitializeUnauthInstance, InitializeAuthInstance } from "../axiosInterceptor";
import { v4 as uuidv4 } from 'uuid';
import { AuthRefreshFailedError } from "../Errors/AuthRefreshFailedError";
import { EmptyApiResponse } from "../../Models/ApiResponse";
import { ErrorHandler } from "../../lib/ErrorHandler";
const IGNORED_API_ERROR_CODES = [
    'ROUTE_NOT_FOUND_ERROR',
    'PRICE_IMPACT_TOO_HIGH',
    'GREATER_THAN_MAX_ERROR',
    'LESS_THAN_MIN_ERROR'
];
class LayerSwapApiClient {
    constructor() {
        this.fetcher = (url) => this.AuthenticatedRequest("GET", url);
        this._authInterceptor = InitializeAuthInstance();
        this._unauthInterceptor = InitializeUnauthInstance(LayerSwapApiClient.apiBaseEndpoint);
    }
    async GetRoutesAsync(direction) {
        return await this.UnauthenticatedRequest("GET", `/${direction}?include_unmatched=true&include_swaps=true&include_unavailable=true`);
    }
    async GetSourceExchangesAsync() {
        return await this.UnauthenticatedRequest("GET", `/source_exchanges`);
    }
    async GetLSNetworksAsync() {
        return await this.UnauthenticatedRequest("GET", `/networks`);
    }
    async CreateSwapAsync(params) {
        const correlationId = uuidv4();
        return await this.AuthenticatedRequest("POST", `/swaps`, params, { 'X-LS-CORRELATION-ID': correlationId });
    }
    async GetTransactionStatus(network, tx_id) {
        return await this.UnauthenticatedRequest("GET", `/transaction_status?network=${network}&transaction_id=${tx_id}`);
    }
    async SwapCatchup(swapId, tx_id) {
        return await this.AuthenticatedRequest("POST", `/swaps/${swapId}/deposit_speedup`, { transaction_id: tx_id });
    }
    async AuthorizeSwapAsync(swapId, signature, signerAddress) {
        return await this.AuthenticatedRequest("POST", `/swaps/${swapId}/authorize`, { signature, signer_address: signerAddress });
    }
    async GetGaslessAuthorizationAsync(swapId) {
        return await this.AuthenticatedRequest("GET", `/swaps/${swapId}/authorize`);
    }
    async GetDepositActionsAsync(swapId, sourceAddress) {
        const query = sourceAddress ? `?source_address=${sourceAddress}` : "";
        return await this.AuthenticatedRequest("GET", `/swaps/${swapId}/deposit_actions${query}`);
    }
    async GetSwapAsync(swapId) {
        return await this.AuthenticatedRequest("GET", `/swaps/${swapId}`);
    }
    async AuthenticatedRequest(method, endpoint, data, header) {
        let uri = LayerSwapApiClient.apiBaseEndpoint + "/api/v2" + endpoint;
        return await this._authInterceptor(uri, { method: method, data: data, headers: { 'Access-Control-Allow-Origin': '*', ...(header ? header : {}) } })
            .then(res => {
            return res?.data;
        })
            .catch(async (reason) => {
            if (reason instanceof AuthRefreshFailedError) {
                return Promise.resolve(new EmptyApiResponse());
            }
            else {
                let error;
                if (reason instanceof Error) {
                    error = reason;
                }
                else {
                    error = new Error(String(reason));
                    error.name = "APIError";
                }
                const errorCode = reason.response?.data?.error?.code;
                if (!IGNORED_API_ERROR_CODES.includes(errorCode)) {
                    ErrorHandler({
                        type: 'APIError',
                        endpoint: endpoint,
                        status: reason.response?.status,
                        statusText: reason.response?.statusText,
                        responseData: reason.response?.data,
                        requestUrl: reason.request?.url,
                        requestMethod: reason.request?.method,
                        message: error.message,
                        name: error.name,
                        stack: error.stack,
                        cause: error.cause
                    });
                }
                return Promise.reject(reason);
            }
        });
    }
    async UnauthenticatedRequest(method, endpoint, data, header) {
        let uri = LayerSwapApiClient.apiBaseEndpoint + "/api/v2" + endpoint;
        return await this._unauthInterceptor(uri, { method: method, data: data, headers: { 'Access-Control-Allow-Origin': '*', ...(header ? header : {}) } })
            .then(res => {
            return res?.data;
        })
            .catch(async (reason) => {
            if (reason instanceof AuthRefreshFailedError) {
                return Promise.resolve(new EmptyApiResponse());
            }
            else {
                console.error("endpoint", reason);
                return Promise.reject(reason);
            }
        });
    }
}
LayerSwapApiClient.apiBaseEndpoint = AppSettings.LayerswapApiUri;
LayerSwapApiClient.apiKey = AppSettings.LayerswapApiKeys[AppSettings.ApiVersion || 'mainnet'];
export default LayerSwapApiClient;
export var DepositAddressSource;
(function (DepositAddressSource) {
    DepositAddressSource[DepositAddressSource["UserGenerated"] = 0] = "UserGenerated";
    DepositAddressSource[DepositAddressSource["Managed"] = 1] = "Managed";
})(DepositAddressSource || (DepositAddressSource = {}));
export var TransactionType;
(function (TransactionType) {
    TransactionType["Input"] = "input";
    TransactionType["Output"] = "output";
    TransactionType["Refuel"] = "refuel";
    TransactionType["Refund"] = "refund";
})(TransactionType || (TransactionType = {}));
export var BackendTransactionStatus;
(function (BackendTransactionStatus) {
    BackendTransactionStatus["Completed"] = "completed";
    BackendTransactionStatus["Failed"] = "failed";
    BackendTransactionStatus["Initiated"] = "initiated";
    BackendTransactionStatus["Pending"] = "pending";
})(BackendTransactionStatus || (BackendTransactionStatus = {}));
export var TransactionStatus;
(function (TransactionStatus) {
    TransactionStatus["Completed"] = "completed";
    TransactionStatus["Failed"] = "failed";
    TransactionStatus["Pending"] = "pending";
})(TransactionStatus || (TransactionStatus = {}));
export var DepositType;
(function (DepositType) {
    DepositType["Manual"] = "manual";
    DepositType["Wallet"] = "wallet";
})(DepositType || (DepositType = {}));
export var SwapType;
(function (SwapType) {
    SwapType["OnRamp"] = "cex_to_network";
    SwapType["OffRamp"] = "network_to_cex";
    SwapType["CrossChain"] = "network_to_network";
})(SwapType || (SwapType = {}));
export var WithdrawType;
(function (WithdrawType) {
    WithdrawType["Wallet"] = "wallet";
    WithdrawType["Manually"] = "manually";
    WithdrawType["Stripe"] = "stripe";
    WithdrawType["Coinbase"] = "coinbase";
    WithdrawType["External"] = "external";
})(WithdrawType || (WithdrawType = {}));
export var SwapStatusInNumbers;
(function (SwapStatusInNumbers) {
    SwapStatusInNumbers[SwapStatusInNumbers["Pending"] = 0] = "Pending";
    SwapStatusInNumbers[SwapStatusInNumbers["Completed"] = 1] = "Completed";
    SwapStatusInNumbers[SwapStatusInNumbers["Failed"] = 2] = "Failed";
    SwapStatusInNumbers[SwapStatusInNumbers["Expired"] = 3] = "Expired";
    SwapStatusInNumbers[SwapStatusInNumbers["Delayed"] = 4] = "Delayed";
    SwapStatusInNumbers[SwapStatusInNumbers["Cancelled"] = 5] = "Cancelled";
    SwapStatusInNumbers["SwapsWithoutCancelledAndExpired"] = "0&status=1&status=2&status=3&status=4";
})(SwapStatusInNumbers || (SwapStatusInNumbers = {}));
